import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  RiAddLine,
  RiArrowDownLine,
  RiArrowUpLine,
  RiDeleteBinLine,
  RiEdit2Line,
  RiRefreshLine,
} from '@remixicon/react';

import { Alert } from '@/design-system/alert';
import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Field } from '@/design-system/field';
import { Hint } from '@/design-system/hint';
import { IconButton } from '@/design-system/icon-button';
import { Label } from '@/design-system/label';
import { SearchSelect } from '@/design-system/search-select';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import { Switch } from '@/design-system/switch';
import { Tag } from '@/design-system/tag';
import AddressEndpointFields, {
  type EndpointField,
  type EndpointValues,
} from '@/pages/addresses/components/AddressEndpointFields';
import KmInput from '@/pages/ujp/components/KmInput';
import { addressesAPI, type Place } from '@/services/api/addresses';
import {
  isWorkflowDrift,
  WORKFLOW_DRIFT_DELETED,
  WORKFLOW_DRIFT_INACTIVE,
  workflowDriftKind,
  type ComputeLegsPayload,
  type RoutePlanLegSource,
  type RoutePlanDrift,
} from '@/services/api/routePlans';
import {
  ROUTE_STOP_INTENTS,
  ROUTE_STOP_TYPES,
  stopKindLabel,
  type RouteStopIntent,
  type RouteStopType,
} from '@/services/api/routeStopKind';
import {
  stopWorkflowsAPI,
  WORKFLOW_STOP_INTENT_LABELS,
  WORKFLOW_STOP_TYPE_LABELS,
  type WorkflowSummary,
} from '@/services/api/stopWorkflows';

import { ROUTE_PLANNER_COPY } from '../copy';
import {
  addPlanRow,
  allPlanRowsLocated,
  applyServerLegs,
  canPinWorkflow,
  clearWorkflowPins,
  derivedKind,
  isPlanRowLocated,
  kmAllLegs,
  kmCharged,
  movePlanRow,
  patchPlanRow,
  planAllPool,
  planChargedOverride,
  removePlanRow,
  resetPlanLegKm,
  setPlanLegKm,
  workflowCandidatesFor,
  type PlanDraft,
  type PlanRowDraft,
} from './routePlanDraft';
import { useRoutePlanLegs } from './useRoutePlanLegs';

/**
 * RoutePlanBuilder (CR-2) — the ordered stop chain of a route plan as a
 * CONTROLLED component over a `PlanDraft`. Moved from the UJP wizard's
 * `UjpRouteBuilder`: same reorder + editable legs, but the place picker
 * now reads the Addresses lane book (`GET /v1/addresses/places`) with a manual
 * fallback, and the leg km comes from the SERVER (`useRoutePlanLegs`) badged by
 * its source — LANE / Diukur (Directions) / Estimasi (haversine) — instead of a
 * browser Distance Matrix.
 */

export interface RoutePlanBuilderProps {
  value: PlanDraft;
  onChange: (next: PlanDraft) => void;
  errors?: Record<string, string>;
  onClearError?: (key: string) => void;
  /** The client the plan belongs to — scopes the place picker. */
  clientId: number | null;
  /**
   * The client's pool-leg rule (`ujp_client_configs.charged_positioning`);
   * `null` while unknown (no banner). It is the DEFAULT the first / last
   * stop's `charged` falls back to (CR-8b); unknown is treated as "tidak
   * ditagih".
   */
  chargedPositioning: boolean | null;
  disabled?: boolean;
  /** Skip the server legs call (tests, or when km is typed by hand). */
  enableLegs?: boolean;
  /**
   * CR-5b: the plan's drift entries (edit mode). Only `field: 'workflow'`
   * rows are rendered here — as a Tag on the affected stop (1-based `seq`).
   * Address drift keeps its alert + "Refresh dari lane" in the drawer.
   */
  drift?: RoutePlanDrift[];
}

/** Tag copy + tone for one stop's workflow drift (CR-5b). */
export const workflowDriftLabel = (
  d: RoutePlanDrift
): { label: string; tone: 'warning' | 'neutral' } => {
  switch (workflowDriftKind(d)) {
    case 'deleted':
      return { label: `Workflow: ${WORKFLOW_DRIFT_DELETED}`, tone: 'warning' };
    case 'inactive':
      return { label: `Workflow: ${WORKFLOW_DRIFT_INACTIVE}`, tone: 'warning' };
    default:
      return { label: `Workflow diganti nama → ${String(d.current)}`, tone: 'neutral' };
  }
};

/**
 * Per-stop kind controls (CR6-D4 / CR-8b): type segmented + intent select; the
 * pool is the first / last stop by position, so there is no pool switch, only
 * a "Ditagih ke client" toggle on those two edge stops.
 */
const INTENT_LABEL = 'Tujuan drop off';
const B = ROUTE_PLANNER_COPY.builder;

const SOURCE_META: Record<
  RoutePlanLegSource,
  { label: string; status: 'success' | 'information' | 'warning' }
> = {
  LANE: { label: 'Lane', status: 'success' },
  DIRECTIONS: { label: 'Diukur', status: 'information' },
  HAVERSINE: { label: 'Estimasi', status: 'warning' },
};

const eyebrow =
  'text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600';

const placeValue = (p: Place) => `${p.addressId}::${p.endpoint}`;

/** The explicit "no pin" row of the workflow picker (clears `workflowId`). */
const WORKFLOW_DEFAULT_OPTION = '__client_default__';
// The resolved default can now be a GLOBAL (client-agnostic) workflow, not
// only the client's own, so the label drops "client".
const WORKFLOW_DEFAULT_LABEL = 'Ikuti default';
/**
 * Under the picker: what the pin means for THIS stop. Since CR8-D5 every stop
 * reaches the shipment, so a pool stop's pin is always used; billing no longer
 * changes that.
 */
const workflowHint = (pool: boolean): string =>
  pool ? B.workflowHintPool : B.workflowHintDefault;

/** CR-5b: "Workflow: dihapus / nonaktif" (warning) or "diganti nama → X" (neutral). */
const WorkflowDriftTag: React.FC<{ seq: number; drift: RoutePlanDrift }> = ({
  seq,
  drift,
}) => {
  const { label, tone } = workflowDriftLabel(drift);
  return (
    <Tag
      variant={tone === 'warning' ? 'stroke' : 'gray'}
      size="xs"
      className={tone === 'warning' ? 'text-warning-dark' : undefined}
      data-testid={`stop-${seq}-workflow-drift`}
      data-tone={tone}
    >
      {label}
    </Tag>
  );
};

const RoutePlanBuilder: React.FC<RoutePlanBuilderProps> = ({
  value,
  onChange,
  errors = {},
  onClearError,
  clientId,
  chargedPositioning,
  disabled = false,
  enableLegs = true,
  drift,
}) => {
  // ── Workflow drift (CR-5b): seq → entry, one per stop at most ──────────────
  const workflowDriftBySeq = useMemo(() => {
    const map = new Map<number, RoutePlanDrift>();
    (drift ?? []).forEach((d) => {
      if (isWorkflowDrift(d) && !map.has(d.seq)) map.set(d.seq, d);
    });
    return map;
  }, [drift]);

  // ── Places (shared across rows) ────────────────────────────────────────────
  const [placeQuery, setPlaceQuery] = useState('');
  const [places, setPlaces] = useState<Place[]>([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [placesError, setPlacesError] = useState<string | null>(null);

  useEffect(() => {
    if (clientId == null) {
      setPlaces([]);
      return;
    }
    let cancelled = false;
    setPlacesLoading(true);
    setPlacesError(null);
    addressesAPI
      .places({
        clientIds: [clientId],
        search: placeQuery || undefined,
        limit: 50,
      })
      .then((rows) => !cancelled && setPlaces(rows))
      .catch((err: unknown) => {
        if (cancelled) return;
        setPlaces([]);
        setPlacesError(err instanceof Error ? err.message : 'Gagal memuat lane');
      })
      .finally(() => !cancelled && setPlacesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [clientId, placeQuery]);

  const placeByValue = useMemo(() => {
    const map = new Map<string, Place>();
    places.forEach((p) => map.set(placeValue(p), p));
    return map;
  }, [places]);

  // ── Workflows (CR-5) — the client's ACTIVE set, fetched once per client and
  // filtered per stop by (type, intent) in `workflowCandidatesFor` (CR6-D1).
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [workflowsError, setWorkflowsError] = useState<string | null>(null);

  useEffect(() => {
    if (clientId == null) {
      setWorkflows([]);
      return;
    }
    let cancelled = false;
    setWorkflowsLoading(true);
    setWorkflowsError(null);
    stopWorkflowsAPI
      // PICKER scope: the client's own workflows UNION the GLOBAL
      // (client-agnostic) ones, so a global workflow is pickable here too.
      .list({ clientID: clientId, includeGlobal: true, status: 'ACTIVE' })
      .then((rows) => !cancelled && setWorkflows(rows))
      .catch((err: unknown) => {
        if (cancelled) return;
        setWorkflows([]);
        setWorkflowsError(
          err instanceof Error ? err.message : 'Gagal memuat workflow'
        );
      })
      .finally(() => !cancelled && setWorkflowsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // A pin belongs to ONE client: when the plan moves to another client, every
  // pin is dropped (the new client's workflows are a different set).
  const prevClientId = useRef(clientId);
  useEffect(() => {
    if (prevClientId.current !== clientId) {
      if (prevClientId.current != null) {
        const next = clearWorkflowPins(value);
        if (next !== value) onChange(next);
      }
      prevClientId.current = clientId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // ── Server legs ────────────────────────────────────────────────────────────
  const legsPayload = useMemo<ComputeLegsPayload | null>(() => {
    if (clientId == null || !allPlanRowsLocated(value)) return null;
    return {
      clientId,
      stops: value.stops.map((s, i) => ({
        seq: i + 1,
        name: s.name,
        addressId: s.addressId,
        endpoint: s.endpoint,
        latitude: s.latitude ?? 0,
        longitude: s.longitude ?? 0,
      })),
    };
  }, [clientId, value]);

  const legs = useRoutePlanLegs(legsPayload, {
    enabled: enableLegs && !disabled,
  });

  // Server answers land on the non-edited legs; `applyServerLegs` returns the
  // same object when nothing changed, so this never loops.
  useEffect(() => {
    if (!legs.legs) return;
    const next = applyServerLegs(value, legs.legs);
    if (next !== value) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legs.legs]);

  // "Hitung otomatis": hand a manual leg back to the server. The answer on hand
  // already belongs to this exact stop chain (a changed address refetches), so
  // apply it at once; with none (pending / failed / never asked) the effect
  // above fills the leg when the answer lands.
  const autoLegKm = (fromUid: string, toUid: string) => {
    const reset = resetPlanLegKm(value, fromUid, toUid);
    if (legs.status === 'ok' && legs.legs) {
      onChange(applyServerLegs(reset, legs.legs));
      return;
    }
    onChange(reset);
    if (legs.status !== 'pending') legs.refresh();
  };

  // CR-8b: pool + billing are DERIVED from position + the edge override, with
  // the client rule as default; an unknown rule reads as "tidak ditagih" (the
  // planner API's own `km_charged` uses the same `false` default). Recomputed
  // every render so reordering moves the edges (and the toggle) along.
  const clientDefault = chargedPositioning ?? false;
  const derived = derivedKind(value, clientDefault);
  const allKm = kmAllLegs(value);
  const chargedKm = kmCharged(value, clientDefault);
  const anyOverride = planChargedOverride(value, clientDefault);
  const allPool = planAllPool(value, clientDefault);

  const patch = (uid: string, p: Partial<PlanRowDraft>) =>
    onChange(patchPlanRow(value, uid, p));

  // ── Per-row manual disclosure ──────────────────────────────────────────────
  const [manualRows, setManualRows] = useState<Record<string, boolean>>({});
  const toggleManual = (uid: string) =>
    setManualRows((prev) => ({ ...prev, [uid]: !prev[uid] }));

  const applyManualField = (
    uid: string,
    field: EndpointField,
    val: string
  ) => {
    const p: Partial<PlanRowDraft> = { addressId: null, endpoint: null };
    if (field === 'lat') p.latitude = toCoord(val);
    else if (field === 'long') p.longitude = toCoord(val);
    else (p as any)[field] = val;
    patch(uid, p);
    onClearError?.(`${uid}.${field === 'name' ? 'name' : 'address'}`);
  };

  const applyManualPatch = (uid: string, patchIn: Partial<EndpointValues>) => {
    const p: Partial<PlanRowDraft> = { addressId: null, endpoint: null };
    if (patchIn.name !== undefined) p.name = patchIn.name;
    if (patchIn.address !== undefined) p.address = patchIn.address;
    if (patchIn.lat !== undefined) p.latitude = toCoord(patchIn.lat);
    if (patchIn.long !== undefined) p.longitude = toCoord(patchIn.long);
    patch(uid, p);
    onClearError?.(`${uid}.name`);
    onClearError?.(`${uid}.address`);
  };

  return (
    <div className="space-y-4">
      {chargedPositioning !== null && (
        <div className="space-y-1">
          <Alert
            status={chargedPositioning ? 'information' : 'warning'}
            appearance="lighter"
            size="sm"
            title={
              chargedPositioning ? B.clientRuleCharged : B.clientRuleUncharged
            }
            data-testid="client-rule"
          />
          {/* The small Alert shows its title only; the CR-8b rule goes under it. */}
          <Hint>
            <span data-testid="client-rule-caption">{B.clientRuleCaption}</span>
          </Hint>
        </div>
      )}

      <div className="flex items-center gap-2 px-1" aria-hidden="true">
        <span className={eyebrow}>Tempat</span>
        <span className="text-text-soft-400">·</span>
        <span className={eyebrow}>Alamat</span>
        <span className="text-text-soft-400">·</span>
        <span className={eyebrow}>Leg</span>
      </div>

      <ol className="space-y-3" aria-label="Daftar stop rute">
        {value.stops.map((stop, index) => {
          const n = index + 1;
          // CR-8b: the derived pool decides the chip / role / hint.
          const d = derived[index];
          const kindLabel = stopKindLabel({ ...stop, pool: d.pool });
          const leg = value.legs[index];
          const legError = leg
            ? errors[`${leg.fromUid}>${leg.toUid}.km`]
            : undefined;
          const manualOpen = !!manualRows[stop.uid] || (!stop.addressId && !!stop.name);
          const effectiveCharged = d.effectiveCharged;
          const overridesRule = d.override;
          return (
            <li key={stop.uid} className="space-y-3">
              <div
                className="rounded-xl border border-stroke-soft-200"
                aria-label={`Stop ${n} · ${kindLabel}`}
                role="group"
              >
                <div className="flex flex-wrap items-center gap-2 rounded-t-xl border-b border-stroke-soft-200 bg-bg-weak-50 px-3 py-2">
                  <span className="font-mono text-[12px] font-semibold tabular-nums text-accent">
                    {String(n).padStart(2, '0')}
                  </span>
                  <SegmentedControl
                    value={stop.type}
                    size="sm"
                    aria-label={`Tipe stop ${n}`}
                    disabled={disabled}
                    onValueChange={(v: string) => {
                      if (v) patch(stop.uid, { type: v as RouteStopType });
                    }}
                  >
                    {ROUTE_STOP_TYPES.map((t) => (
                      <SegmentedItem key={t} value={t} size="sm">
                        {WORKFLOW_STOP_TYPE_LABELS[t]}
                      </SegmentedItem>
                    ))}
                  </SegmentedControl>
                  <Tag
                    variant={d.pool ? 'gray' : 'stroke'}
                    size="xs"
                    data-testid={`stop-${n}-kind`}
                  >
                    {kindLabel}
                  </Tag>
                  <div className="ml-auto flex items-center gap-1">
                    {!isPlanRowLocated(stop) && (
                      <Tag variant="stroke" size="xs" className="text-warning-dark">
                        Perlu lokasi
                      </Tag>
                    )}
                    {!stop.addressId && stop.name.trim() !== '' && (
                      <Tag variant="gray" size="xs">
                        Manual (DRAFT)
                      </Tag>
                    )}
                    {overridesRule && (
                      <Tag
                        variant="stroke"
                        size="xs"
                        className="text-warning-dark"
                        data-testid={`stop-${n}-charged-override`}
                      >
                        {B.chargedOverride}
                      </Tag>
                    )}
                    {stop.workflowId && (
                      <Tag
                        variant="stroke"
                        size="xs"
                        data-testid={`stop-${n}-workflow-tag`}
                      >
                        Workflow · {stop.workflowName ?? '…'}
                      </Tag>
                    )}
                    {workflowDriftBySeq.has(n) && (
                      <WorkflowDriftTag
                        seq={n}
                        drift={workflowDriftBySeq.get(n) as RoutePlanDrift}
                      />
                    )}
                    <IconButton
                      tone="neutral"
                      style="ghost"
                      size="xs"
                      aria-label={`Naikkan stop ${n}`}
                      disabled={disabled || index === 0}
                      onClick={() => onChange(movePlanRow(value, stop.uid, -1))}
                    >
                      <RiArrowUpLine />
                    </IconButton>
                    <IconButton
                      tone="neutral"
                      style="ghost"
                      size="xs"
                      aria-label={`Turunkan stop ${n}`}
                      disabled={disabled || index === value.stops.length - 1}
                      onClick={() => onChange(movePlanRow(value, stop.uid, 1))}
                    >
                      <RiArrowDownLine />
                    </IconButton>
                    <IconButton
                      tone="destructive"
                      style="ghost"
                      size="xs"
                      aria-label={`Hapus stop ${n}`}
                      disabled={disabled || value.stops.length <= 2}
                      onClick={() => onChange(removePlanRow(value, stop.uid))}
                    >
                      <RiDeleteBinLine />
                    </IconButton>
                  </div>
                </div>

                <div className="space-y-3 px-3 py-3">
                  {/* Kind (CR6-D4): intent for a Drop off. */}
                  {stop.type === 'DROP_OFF' && (
                    <div className="flex flex-wrap items-end gap-3">
                      <Field className="min-w-[200px] flex-1">
                        <Label id={`${stop.uid}-intent-label`}>{INTENT_LABEL}</Label>
                        <div
                          role="group"
                          aria-labelledby={`${stop.uid}-intent-label`}
                          data-testid={`stop-${n}-intent`}
                        >
                          <SearchSelect
                            options={ROUTE_STOP_INTENTS.map((i) => ({
                              value: i,
                              label: WORKFLOW_STOP_INTENT_LABELS[i],
                            }))}
                            value={stop.intent}
                            onChange={(v) => {
                              const intent = String(v ?? '') as RouteStopIntent;
                              if (ROUTE_STOP_INTENTS.includes(intent)) {
                                patch(stop.uid, { intent });
                              }
                            }}
                            placeholder={INTENT_LABEL}
                            disabled={disabled}
                          />
                        </div>
                      </Field>
                    </div>
                  )}

                  {/* Billing (CR-8b): the first and last stop are the pool;
                      "Ditagih ke client" follows the rule or overrides it in
                      either direction. Middle stops are always billed and
                      show nothing. */}
                  {d.isEdge && (
                    <Field>
                      <div className="flex flex-wrap items-center gap-3">
                        <label className="flex items-center gap-2 text-[13px] font-medium">
                          <Switch
                            checked={effectiveCharged}
                            disabled={disabled}
                            aria-label={`${B.chargedControl} stop ${n}`}
                            data-testid={`stop-${n}-charged`}
                            onCheckedChange={(v) =>
                              patch(stop.uid, { charged: v === true })
                            }
                          />
                          {B.chargedControl}
                        </label>
                        <span
                          className="text-[12px] text-text-sub-600"
                          data-testid={`stop-${n}-charged-rule`}
                        >
                          {effectiveCharged ? B.chargedYes : B.chargedNo}
                          {' · '}
                          {B.clientRuleLine(clientDefault)}
                        </span>
                        {stop.charged != null && (
                          <button
                            type="button"
                            className="text-[12px] font-semibold text-primary-base underline-offset-2 hover:underline"
                            disabled={disabled}
                            onClick={() => patch(stop.uid, { charged: null })}
                          >
                            {B.chargedFollowRule}
                          </button>
                        )}
                      </div>
                    </Field>
                  )}

                  <Field>
                    <Label id={`${stop.uid}-place-label`}>Pilih dari lane</Label>
                    <div role="group" aria-labelledby={`${stop.uid}-place-label`}>
                      <SearchSelect
                        options={places.map((p) => ({
                          value: placeValue(p),
                          label: `${p.name} · ${p.address}`,
                        }))}
                        value={
                          stop.addressId
                            ? `${stop.addressId}::${stop.endpoint}`
                            : undefined
                        }
                        selectedLabelFallback={
                          stop.addressId ? stop.name : undefined
                        }
                        onChange={(v) => {
                          const p = placeByValue.get(String(v));
                          if (!p) return;
                          patch(stop.uid, {
                            name: p.name,
                            address: p.address,
                            latitude: p.latitude,
                            longitude: p.longitude,
                            addressId: p.addressId,
                            endpoint: p.endpoint,
                          });
                          onClearError?.(`${stop.uid}.name`);
                          onClearError?.(`${stop.uid}.address`);
                        }}
                        placeholder={
                          clientId == null
                            ? 'Pilih client dulu…'
                            : 'Cari tempat di lane…'
                        }
                        disabled={disabled || clientId == null}
                        loading={placesLoading}
                        disableLocalFilter
                        onSearchChange={setPlaceQuery}
                        emptyMessage={
                          placesError ??
                          'Lane tidak ditemukan — isi manual di bawah'
                        }
                      />
                    </div>
                    {stop.addressId && (
                      <Hint>{stop.address}</Hint>
                    )}
                  </Field>

                  <div>
                    <Button
                      tone="neutral"
                      style="ghost"
                      size="xs"
                      disabled={disabled}
                      onClick={() => toggleManual(stop.uid)}
                    >
                      <RiEdit2Line />
                      {manualOpen ? 'Sembunyikan isian manual' : 'Isi manual'}
                    </Button>
                    {manualOpen && (
                      <div className="mt-2">
                        <AddressEndpointFields
                          role={stop.type === 'PICKUP' ? 'origin' : 'destination'}
                          modes={['manual']}
                          label={`Stop ${n} · ${kindLabel}`}
                          color={stop.type === 'PICKUP' ? '#5E2AAC' : '#1FC16B'}
                          open
                          idPrefix={`${stop.uid}-loc`}
                          ariaLabelPrefix={`Stop ${n}`}
                          placeholders={{
                            name: 'mis. Gudang Karawang',
                            address: 'mis. Jl. Permata Raya Lot 12, KIIC',
                          }}
                          values={{
                            name: stop.name,
                            address: stop.address,
                            lat:
                              stop.latitude != null ? String(stop.latitude) : '',
                            long:
                              stop.longitude != null
                                ? String(stop.longitude)
                                : '',
                          }}
                          errors={{
                            name: errors[`${stop.uid}.name`],
                            address: errors[`${stop.uid}.address`],
                          }}
                          onFieldChange={(field, val) =>
                            applyManualField(stop.uid, field, val)
                          }
                          onPatch={(p) => applyManualPatch(stop.uid, p)}
                        />
                      </div>
                    )}
                  </div>

                  {/* Workflow pin (CR-5) — every stop; the hint says what it means. */}
                  {canPinWorkflow(stop) && (
                    <Field>
                      <Label>Workflow</Label>
                      <div role="group" aria-label={`Workflow stop ${n}`}>
                        <SearchSelect
                          options={[
                            {
                              value: WORKFLOW_DEFAULT_OPTION,
                              label: WORKFLOW_DEFAULT_LABEL,
                            },
                            ...workflowCandidatesFor(stop, workflows).map((w) => ({
                              value: w.id,
                              label: w.isDefault ? `${w.name} (default)` : w.name,
                            })),
                          ]}
                          value={stop.workflowId ?? undefined}
                          selectedLabelFallback={stop.workflowName ?? undefined}
                          onChange={(v) => {
                            const id = String(v ?? '');
                            if (!id || id === WORKFLOW_DEFAULT_OPTION) {
                              patch(stop.uid, { workflowId: null, workflowName: null });
                              return;
                            }
                            const w = workflows.find((x) => x.id === id);
                            if (!w) return;
                            patch(stop.uid, { workflowId: w.id, workflowName: w.name });
                          }}
                          placeholder={
                            clientId == null
                              ? 'Pilih client dulu…'
                              : WORKFLOW_DEFAULT_LABEL
                          }
                          disabled={disabled || clientId == null}
                          loading={workflowsLoading}
                          emptyMessage={
                            workflowsError ??
                            (clientId == null
                              ? 'Pilih client dulu'
                              : 'Belum ada workflow aktif untuk stop ini')
                          }
                        />
                      </div>
                      <Hint>
                        <span data-testid={`stop-${n}-workflow-note`}>
                          {workflowHint(d.pool)}
                        </span>
                      </Hint>
                    </Field>
                  )}
                </div>
              </div>

              {/* Leg out of this stop */}
              {leg && (
                <div
                  className="ml-6 flex flex-wrap items-center gap-2 border-l-2 border-dashed border-stroke-soft-200 pl-4"
                  data-testid={`leg-${index + 1}`}
                >
                  <span className="text-[12px] text-text-sub-600">
                    Leg {n} → {n + 1}
                  </span>
                  <div className="w-40 shrink-0">
                    <KmInput
                      id={`${leg.fromUid}-${leg.toUid}-km`}
                      aria-label={`Km leg ${n} ke ${n + 1}`}
                      size="sm"
                      value={leg.km}
                      disabled={disabled}
                      invalid={!!legError}
                      locked={!leg.edited && leg.km !== ''}
                      onChange={(km) => {
                        onChange(setPlanLegKm(value, leg.fromUid, leg.toUid, km));
                        onClearError?.(`${leg.fromUid}>${leg.toUid}.km`);
                      }}
                    />
                  </div>
                  {leg.edited ? (
                    <>
                      <Badge status="neutral" size="sm">
                        Diubah manual
                      </Badge>
                      <Button
                        tone="neutral"
                        style="ghost"
                        size="xs"
                        disabled={disabled || legsPayload == null}
                        onClick={() => autoLegKm(leg.fromUid, leg.toUid)}
                        aria-label={`Hitung otomatis leg ${n} ke ${n + 1}`}
                        title={
                          legsPayload == null
                            ? 'Lengkapi lokasi semua stop dulu'
                            : 'Hitung ulang km leg ini dari alamat stop'
                        }
                        data-testid={`leg-${index + 1}-auto`}
                      >
                        <RiRefreshLine />
                        Hitung otomatis
                      </Button>
                    </>
                  ) : leg.source ? (
                    <Badge status={SOURCE_META[leg.source].status} size="sm">
                      {SOURCE_META[leg.source].label}
                    </Badge>
                  ) : legs.loading && leg.km === '' ? (
                    <span className="text-[11px] text-text-sub-600">…</span>
                  ) : null}
                  {legError && <Hint tone="error">{legError}</Hint>}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {legs.status === 'error' && (
        <Hint tone="warning">
          Perhitungan jarak server gagal — isi km tiap leg secara manual.
        </Hint>
      )}
      {errors.plan && <Hint tone="error">{errors.plan}</Hint>}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          tone="neutral"
          style="stroke"
          size="sm"
          disabled={disabled}
          onClick={() => onChange(addPlanRow(value, { type: 'DROP_OFF' }))}
        >
          <RiAddLine />
          Tambah stop
        </Button>
        <dl className="flex flex-wrap items-center gap-5 rounded-xl border border-stroke-soft-200 bg-bg-weak-50 px-4 py-2">
          <div>
            <dt className={eyebrow}>{B.kmAllLegs}</dt>
            <dd
              className="font-mono text-[14px] font-semibold tabular-nums text-text-strong-950"
              data-testid="km-all"
            >
              {allKm.toLocaleString('id-ID')}
            </dd>
          </div>
          <div>
            <dt className={eyebrow}>{B.kmCharged}</dt>
            <dd
              className="flex items-center gap-2 font-mono text-[14px] font-semibold tabular-nums text-text-strong-950"
              data-testid="km-charged"
            >
              {chargedKm.toLocaleString('id-ID')}
              {anyOverride && (
                <Tag
                  variant="stroke"
                  size="xs"
                  className="font-sans text-warning-dark"
                  data-testid="km-charged-override"
                >
                  {B.chargedOverride}
                </Tag>
              )}
            </dd>
          </div>
        </dl>
      </div>
      {allPool && (
        <Hint tone="warning">
          <span data-testid="km-all-pool-hint">{B.allPoolHint}</span>
        </Hint>
      )}
    </div>
  );
};

const toCoord = (v: string): number | undefined => {
  if (v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
};

export default RoutePlanBuilder;
